import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Flex,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  DangerButton,
  OutlineButton,
  PrimaryButton,
} from '../../../../components/ui';
import { formatDateTimeDisplay } from '../../../../utils/personName';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  deleteEquipmentKyc,
  updateEquipmentKyc,
} from '../../../../repositories/equipmentKyc.repository';

export function useKycCrud({ onSaved, onDeleted } = {}) {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canEdit = hasPermission('equipment', 'edit');
  const canDelete = hasPermission('equipment', 'delete');

  const confirmDelete = async () => {
    if (!deleteRow?.id || deleting) return;
    setDeleting(true);
    try {
      await deleteEquipmentKyc(deleteRow.id);
      setDeleteRow(null);
      toast({ title: 'KYC deleted', status: 'success' });
      onDeleted?.(deleteRow);
    } catch (err) {
      toast({ title: err?.message || 'Delete failed', status: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const dialogs = (
    <>
      <KycEditModal
        isOpen={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        row={editRow}
        onSaved={(saved) => {
          toast({ title: 'KYC updated', status: 'success' });
          onSaved?.(saved);
        }}
      />
      <KycDeleteDialog
        isOpen={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        row={deleteRow}
        busy={deleting}
        onConfirm={confirmDelete}
      />
    </>
  );

  return { canEdit, canDelete, setEditRow, setDeleteRow, dialogs };
}

export const KYC_SORT_OPTIONS = [
  { value: 'submittedAt:DESC', label: 'Newest first' },
  { value: 'submittedAt:ASC', label: 'Oldest first' },
  { value: 'email:ASC', label: 'Email A–Z' },
  { value: 'email:DESC', label: 'Email Z–A' },
  { value: 'phone:ASC', label: 'Phone A–Z' },
  { value: 'phone:DESC', label: 'Phone Z–A' },
];

export function parseKycSort(value) {
  const [sortBy, sortDir] = String(value || 'submittedAt:DESC').split(':');
  return { sortBy: sortBy || 'submittedAt', sortDir: sortDir || 'DESC' };
}

export function KycListFilters({
  search,
  onSearch,
  submittedFrom,
  submittedTo,
  onFrom,
  onTo,
  sortValue,
  onSort,
  total,
  onClear,
}) {
  const field = {
    size: 'sm',
    h: '32px',
    bg: 'bg.input',
    borderColor: 'border.subtle',
    color: 'text.primary',
    _placeholder: { color: 'text.placeholder' },
    _hover: { borderColor: 'border.subtle' },
    _focus: { borderColor: 'utkarsh.amber', boxShadow: 'none' },
  };
  return (
    <Flex gap={2} flexWrap="wrap" align="center" mb={4}>
      <Input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search email or phone"
        maxW={{ base: '100%', md: '280px' }}
        {...field}
      />
      <Box w="158px">
        <AppDatePicker
          label=""
          helperText=""
          size="sm"
          placeholder="From date"
          value={submittedFrom}
          onChange={onFrom}
        />
      </Box>
      <Box w="158px">
        <AppDatePicker
          label=""
          helperText=""
          size="sm"
          placeholder="To date"
          value={submittedTo}
          onChange={onTo}
        />
      </Box>
      <Select
        value={sortValue}
        onChange={(e) => onSort(e.target.value)}
        maxW="180px"
        {...field}
      >
        {KYC_SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value} style={{ color: '#111' }}>
            {opt.label}
          </option>
        ))}
      </Select>
      {onClear ? (
        <OutlineButton size="sm" h="32px" minH="32px" onClick={onClear}>
          Clear
        </OutlineButton>
      ) : null}
      <Text fontSize="13px" fontWeight="600" color="text.secondary" ml={{ md: 'auto' }}>
        {total} {total === 1 ? 'submission' : 'submissions'}
      </Text>
    </Flex>
  );
}

function FileCard({ title, file, onOpen }) {
  if (!file) return null;
  const image = String(file.mimeType || '').startsWith('image/');
  const openUrl = file.viewUrl;
  const preview = image && openUrl ? (
    <Image
      src={openUrl}
      alt={file.fileName}
      w="100%"
      h={{ base: '220px', md: '280px' }}
      objectFit="contain"
      bg="white"
      pointerEvents="none"
    />
  ) : (
    <Flex h="140px" align="center" justify="center" px={3} pointerEvents="none">
      <Text fontSize="13px" fontWeight="600" noOfLines={2} textAlign="center">
        {file.fileName}
      </Text>
    </Flex>
  );
  return (
    <Box
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      overflow="hidden"
      bg="bg.input"
    >
      {title ? (
        <Text fontSize="12px" fontWeight="700" px={3} pt={2} pb={1}>
          {title}
        </Text>
      ) : null}
      {openUrl ? (
        <Box
          as="button"
          type="button"
          display="block"
          w="100%"
          p={0}
          border="0"
          bg="transparent"
          cursor="pointer"
          onClick={() => onOpen?.({ ...file, title })}
          title="Open"
        >
          {preview}
        </Box>
      ) : (
        preview
      )}
      <Box p={2}>
        <Text fontSize="12px" noOfLines={1} title={file.fileName}>
          {file.fileName}
        </Text>
      </Box>
    </Box>
  );
}

function FileGrid({ title, files, onOpen }) {
  const list = Array.isArray(files) ? files : [];
  return (
    <Box>
      <Text fontSize="13px" fontWeight="700" mb={3}>
        {title}
      </Text>
      {list.length ? (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
          {list.map((file) => (
            <FileCard key={file.id} file={file} onOpen={onOpen} />
          ))}
        </SimpleGrid>
      ) : (
        <Text color="text.secondary" fontSize="14px">
          No files
        </Text>
      )}
    </Box>
  );
}

function KycFilePreviewDialog({ file, onClose }) {
  if (!file) return null;
  const image = String(file.mimeType || '').startsWith('image/');
  return (
    <Modal isOpen={Boolean(file)} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="bg.surface" mx={4} maxH="92vh">
        <ModalHeader>{file.title || file.fileName || 'Document'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {image && file.viewUrl ? (
            <Image
              src={file.viewUrl}
              alt={file.fileName}
              w="100%"
              maxH="78vh"
              objectFit="contain"
              bg="white"
              borderRadius="md"
            />
          ) : file.viewUrl ? (
            <Box
              as="iframe"
              src={file.viewUrl}
              title={file.fileName}
              w="100%"
              h="78vh"
              border="0"
              bg="white"
            />
          ) : (
            <Text color="text.secondary">File is not available.</Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export function KycDocumentSection({ rcFiles = [], aadhaarFiles = [] }) {
  const [preview, setPreview] = useState(null);
  const rc = Array.isArray(rcFiles) ? rcFiles : [];
  const aadhaar = Array.isArray(aadhaarFiles) ? aadhaarFiles : [];
  return (
    <>
      {rc.length === 1 && aadhaar.length === 1 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FileCard title="Registration Certificate (RC)" file={rc[0]} onOpen={setPreview} />
          <FileCard title="Aadhaar" file={aadhaar[0]} onOpen={setPreview} />
        </SimpleGrid>
      ) : (
        <Box display="grid" gap={4}>
          <FileGrid title="Registration Certificate (RC)" files={rc} onOpen={setPreview} />
          <FileGrid title="Aadhaar" files={aadhaar} onOpen={setPreview} />
        </Box>
      )}
      <KycFilePreviewDialog file={preview} onClose={() => setPreview(null)} />
    </>
  );
}

function itemSource(item) {
  return `${item?.source || ''} ${item?.key || ''} ${item?.keyUi || ''}`.toLowerCase();
}

function splitExtractedItems(items) {
  const rc = [];
  const aadhaar = [];
  const other = [];
  for (const item of items) {
    const blob = itemSource(item);
    if (blob.includes('aadhaar') || blob.includes('aadhar')) aadhaar.push(item);
    else if (blob.includes('rc_book') || blob.includes('rc ') || blob.startsWith('rc_') || blob.includes('registration')) {
      rc.push(item);
    } else {
      other.push(item);
    }
  }
  return { rc, aadhaar, other };
}

function ExtractedGroup({ title, items }) {
  if (!items.length) {
    return (
      <Box>
        <Text fontSize="13px" fontWeight="700" mb={2}>
          {title}
        </Text>
        <Text fontSize="13px" color="text.secondary">
          No fields from this document.
        </Text>
      </Box>
    );
  }
  return (
    <Box>
      <Text fontSize="13px" fontWeight="700" mb={2}>
        {title}
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={2}>
        {items.map((item) => (
          <Box
            key={`${item.source || ''}-${item.key || item.keyUi}`}
            px={3}
            py={2}
            bg="bg.input"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
          >
            <Text fontSize="12px" color="text.secondary" mb={0.5} fontWeight="500">
              {item.keyUi || item.key || 'Field'}
            </Text>
            <Text fontSize="15px" fontWeight="600" whiteSpace="pre-wrap">
              {item.value || '—'}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export function KycExtractedSection({ extractedData, onExtract, extracting = false }) {
  const data = extractedData && typeof extractedData === 'object' ? extractedData : null;
  const items = Array.isArray(data?.items) ? data.items : [];
  const status = data?.status || (items.length ? 'success' : null);
  const extractedAt = data?.extractedAt || null;
  const showExtract = Boolean(onExtract) && status !== 'pending' && !items.length;

  let body = null;
  if (status === 'pending' || extracting) {
    body = (
      <Text fontSize="13px" color="text.secondary" py={2}>
        Extracting fields from documents…
      </Text>
    );
  } else if (status === 'error') {
    body = (
      <Text fontSize="13px" color="status.error" py={2}>
        {data?.error || 'AI extract failed'}
      </Text>
    );
  } else if (!items.length) {
    body = (
      <Text fontSize="13px" color="text.secondary" py={2}>
        No extracted data yet.
      </Text>
    );
  } else {
    const grouped = splitExtractedItems(items);
    body = (
      <Box display="grid" gap={5} mt={2}>
        <ExtractedGroup title="Registration Certificate (RC)" items={grouped.rc} />
        <ExtractedGroup title="Aadhaar" items={grouped.aadhaar} />
        {grouped.other.length ? <ExtractedGroup title="Other" items={grouped.other} /> : null}
      </Box>
    );
  }

  return (
    <Box>
      <Flex align="baseline" justify="space-between" gap={2} mb={1} wrap="wrap">
        <Text fontSize="14px" fontWeight="700">
          Extracted Data (AI)
        </Text>
        <Flex align="center" gap={3}>
          {extractedAt ? (
            <Text fontSize="12px" color="text.secondary">
              {formatDateTimeDisplay(extractedAt)}
            </Text>
          ) : null}
          {showExtract ? (
            <OutlineButton size="sm" onClick={onExtract} isLoading={extracting}>
              Extract data
            </OutlineButton>
          ) : null}
        </Flex>
      </Flex>
      {body}
    </Box>
  );
}

export function KycActionButtons({ canEdit, canDelete, onEdit, onDelete }) {
  if (!canEdit && !canDelete) return null;
  return (
    <Flex
      gap={2}
      justify="flex-end"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {canEdit ? (
        <OutlineButton size="xs" h="28px" minH="28px" onClick={onEdit}>
          Edit
        </OutlineButton>
      ) : null}
      {canDelete ? (
        <DangerButton size="xs" h="28px" minH="28px" onClick={onDelete}>
          Delete
        </DangerButton>
      ) : null}
    </Flex>
  );
}

export function KycEditModal({ isOpen, onClose, row, onSaved }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rcFiles, setRcFiles] = useState(null);
  const [aadhaarFiles, setAadhaarFiles] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail(row?.email || '');
    setPhone(String(row?.phone || '').replace(/\D/g, '').slice(-10));
    setRcFiles(null);
    setAadhaarFiles(null);
    setError('');
  }, [isOpen, row]);

  const save = async () => {
    setError('');
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email');
      return;
    }
    if (!/^\d{10}$/.test(phone)) {
      setError('Enter a 10-digit mobile number');
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('email', email.trim());
      form.append('phone', phone);
      Array.from(rcFiles || []).forEach((file) => form.append('rcFiles', file));
      Array.from(aadhaarFiles || []).forEach((file) => form.append('aadhaarFiles', file));
      const saved = await updateEquipmentKyc(row.id, form);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Equipment KYC</ModalHeader>
        {!saving ? <ModalCloseButton /> : null}
        <ModalBody>
          <Box display="grid" gap={4}>
            <AppInput
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
            />
            <AppInput
              label="Phone"
              leftAddon="+91"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              isRequired
            />
            <Box>
              <Text fontSize="12px" fontWeight="600" mb={1}>
                Replace RC files
              </Text>
              <Input
                type="file"
                multiple
                accept="image/*,.pdf"
                size="sm"
                onChange={(e) => setRcFiles(e.target.files)}
              />
              <Text fontSize="12px" color="text.secondary" mt={1}>
                Leave empty to keep current RC files.
              </Text>
            </Box>
            <Box>
              <Text fontSize="12px" fontWeight="600" mb={1}>
                Replace Aadhaar files
              </Text>
              <Input
                type="file"
                multiple
                accept="image/*,.pdf"
                size="sm"
                onChange={(e) => setAadhaarFiles(e.target.files)}
              />
              <Text fontSize="12px" color="text.secondary" mt={1}>
                Leave empty to keep current Aadhaar files.
              </Text>
            </Box>
            {error ? (
              <Text color="status.error" fontSize="13px">
                {error}
              </Text>
            ) : null}
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <OutlineButton onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton onClick={save} isLoading={saving}>
            Save
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function KycDeleteDialog({ isOpen, onClose, row, onConfirm, busy }) {
  const cancelRef = useRef(null);
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={busy ? () => {} : onClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="700">
            Delete this KYC?
          </AlertDialogHeader>
          <AlertDialogBody>
            {row?.email || 'This submission'} will be removed from the list.
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <OutlineButton ref={cancelRef} onClick={onClose} isDisabled={busy}>
              Cancel
            </OutlineButton>
            <DangerButton onClick={onConfirm} isLoading={busy}>
              Delete
            </DangerButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
